import React, { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { getTodayEastern, getDaysAgoEastern } from '../../lib/dateHelpers'
import TimeEditModal from './TimeEditModal'

function fmtET(iso) {
  if (!iso) return '--'
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })
}
function calcElapsed(clockIn) {
  var ms = Date.now() - new Date(clockIn).getTime()
  if (ms < 0) ms = 0
  var h = Math.floor(ms / 3600000)
  var m = Math.floor((ms % 3600000) / 60000)
  return h + 'h ' + m + 'm'
}
function calcElapsedHrs(clockIn) {
  var ms = Date.now() - new Date(clockIn).getTime()
  return Math.max(0, ms / 3600000)
}

export default function DailyClockIn() {
  var { user } = useAuth()
  var [openSession, setOpenSession] = useState(null)
  var [todaySessions, setTodaySessions] = useState([])
  var [recentDays, setRecentDays] = useState([])
  var [expandedDay, setExpandedDay] = useState(null)
  var [expandedSessions, setExpandedSessions] = useState([])
  var [elapsed, setElapsed] = useState('')
  var [loading, setLoading] = useState(true)
  var [actionLoading, setActionLoading] = useState(false)
  var [error, setError] = useState(null)
  var [editModal, setEditModal] = useState(null)
  var intervalRef = useRef(null)

  var today = getTodayEastern()

  var fetchAll = useCallback(async function() {
    if (!user?.id) return

    // Find ANY open session for this user, regardless of work_date. This is the
    // only way to survive the UTC-vs-ET rollover: a session opened at 4 PM ET
    // April 21 has work_date = 2026-04-21, but at 10 PM ET the UTC date is
    // already April 22 — a date-filtered query would miss it.
    var { data: open } = await supabase.from('daily_attendance').select('*')
      .eq('user_id', user.id).eq('status', 'clocked_in').is('clock_out', null)
      .order('clock_in', { ascending: false }).limit(1).maybeSingle()
    setOpenSession(open || null)
    if (open) setElapsed(calcElapsed(open.clock_in))

    // Today's sessions (ET date). If the open session rolled over from
    // yesterday in ET, prepend it so it still renders in the "Today" list.
    var { data: todayRows } = await supabase.from('daily_attendance').select('*')
      .eq('user_id', user.id).eq('work_date', today)
      .order('session_number', { ascending: true })
    var allSessions = todayRows || []
    if (open && !allSessions.find(function(r) { return r.id === open.id })) {
      allSessions = [open].concat(allSessions)
    }
    setTodaySessions(allSessions)

    // Recent days (grouped) — last 6 days excluding today. Filter out the open
    // session if it belongs to one of those days (it's already shown on top).
    var sixAgo = getDaysAgoEastern(6)
    var { data: recent } = await supabase.from('daily_attendance').select('*')
      .eq('user_id', user.id).gte('work_date', sixAgo).lt('work_date', today)
      .order('work_date', { ascending: false }).order('session_number', { ascending: true })
    var recentFiltered = (recent || []).filter(function(r) { return !open || r.id !== open.id })
    var grouped = {}
    recentFiltered.forEach(function(r) {
      if (!grouped[r.work_date]) grouped[r.work_date] = []
      grouped[r.work_date].push(r)
    })
    var dayList = Object.keys(grouped).sort().reverse().slice(0, 5).map(function(d) {
      var sessions2 = grouped[d]
      var total = sessions2.reduce(function(s, r) { return s + (parseFloat(r.total_hours) || 0) }, 0)
      return { date: d, sessions: sessions2, total: total, count: sessions2.length }
    })
    setRecentDays(dayList)
    setLoading(false)
  }, [user?.id, today])

  useEffect(function() { fetchAll() }, [fetchAll])

  // Recalculate elapsed every 30s from DB timestamp
  useEffect(function() {
    if (intervalRef.current) clearInterval(intervalRef.current)
    if (openSession && openSession.clock_in) {
      setElapsed(calcElapsed(openSession.clock_in))
      intervalRef.current = setInterval(function() {
        setElapsed(calcElapsed(openSession.clock_in))
      }, 30000)
    }
    return function() { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [openSession])

  // CLOCK IN — creates a new session
  var handleClockIn = async function() {
    if (actionLoading) return
    setActionLoading(true); setError(null)
    try {
      var nextSession = (todaySessions.length || 0) + 1
      var { error: err } = await supabase.from('daily_attendance').insert({
        user_id: user.id, work_date: today, clock_in: new Date().toISOString(),
        status: 'clocked_in', session_number: nextSession
      })
      if (err) { setError('Clock in failed: ' + err.message); console.error(err) }
      else { await fetchAll() }
    } catch (e) { setError('No connection \u2014 try again'); console.error(e) }
    setActionLoading(false)
  }

  // CLOCK OUT — closes the open session
  var handleClockOut = async function() {
    if (actionLoading || !openSession) return
    setActionLoading(true); setError(null)
    try {
      var { error: err } = await supabase.from('daily_attendance').update({
        clock_out: new Date().toISOString(), status: 'clocked_out'
      }).eq('id', openSession.id)
      if (err) { setError('Clock out failed: ' + err.message); console.error(err) }
      else { await fetchAll() }
    } catch (e) { setError('No connection \u2014 try again'); console.error(e) }
    setActionLoading(false)
  }

  // Expand a past day to see its sessions
  var toggleDay = async function(dateStr) {
    if (expandedDay === dateStr) { setExpandedDay(null); return }
    setExpandedDay(dateStr)
    var day = recentDays.find(function(d) { return d.date === dateStr })
    setExpandedSessions(day ? day.sessions : [])
  }

  // Today's total hours (completed sessions + live elapsed)
  var todayCompletedHrs = todaySessions.filter(function(s) { return s.clock_out }).reduce(function(sum, s) { return sum + (parseFloat(s.total_hours) || 0) }, 0)
  var todayLiveHrs = openSession ? calcElapsedHrs(openSession.clock_in) : 0
  var todayTotal = todayCompletedHrs + todayLiveHrs

  if (loading) return <div style={st.wrap}><div style={{ color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: '20px' }}>Loading...</div></div>

  return (
    <div style={st.wrap}>
      {error && <div style={st.errorBox}>{error}</div>}

      {/* === BUTTON: CLOCK IN or CLOCK OUT === */}
      {openSession ? (
        <button onClick={handleClockOut} disabled={actionLoading} style={{ ...st.btn, backgroundColor: actionLoading ? '#555' : '#ef4444' }}>
          {actionLoading ? <span style={st.spinRow}><span style={st.spinner} />CLOCKING OUT...</span> : 'CLOCK OUT \u2014 ' + elapsed}
        </button>
      ) : (
        <button onClick={handleClockIn} disabled={actionLoading} style={{ ...st.btn, backgroundColor: actionLoading ? '#555' : '#22c55e' }}>
          {actionLoading ? <span style={st.spinRow}><span style={st.spinner} />CLOCKING IN...</span> : (todaySessions.length > 0 ? 'CLOCK IN (NEW SESSION)' : 'CLOCK IN')}
        </button>
      )}

      {/* === TODAY'S SESSIONS === */}
      {todaySessions.length > 0 && (
        <div style={st.sessionsBox}>
          <div style={st.sessionsTitle}>TODAY \u2014 {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</div>
          {todaySessions.map(function(s) {
            var isLive = s.status === 'clocked_in' && !s.clock_out
            var hrs = isLive ? calcElapsedHrs(s.clock_in) : (parseFloat(s.total_hours) || 0)
            var threeDaysAgo = getDaysAgoEastern(3)
            var canEdit = s.work_date >= threeDaysAgo
            return (
              <div key={s.id} style={st.sessionRow}>
                <span style={st.sessionLabel}>Session {s.session_number}:</span>
                {canEdit ? <button onClick={function() { setEditModal({ record: s, field: 'clock_in' }) }} style={st.goldTime}>{fmtET(s.clock_in)}</button> : <span style={{ color: '#22c55e', fontSize: '13px' }}>{fmtET(s.clock_in)}</span>}
                <span style={{ color: 'rgba(255,255,255,0.15)', margin: '0 4px' }}>\u2014</span>
                {isLive ? (
                  <span style={{ color: '#b48c3c', fontSize: '13px' }}>{String.fromCodePoint(0x23F1)} {elapsed}</span>
                ) : (
                  canEdit ? <button onClick={function() { setEditModal({ record: s, field: 'clock_out' }) }} style={st.goldTime}>{fmtET(s.clock_out)}</button> : <span style={{ color: '#ef4444', fontSize: '13px' }}>{fmtET(s.clock_out)}</span>
                )}
                <span style={st.sessionHrs}>({Number(hrs).toFixed(2)} hrs)</span>
                {s.edited_at && <span style={st.editedBadge}>Edited</span>}
              </div>
            )
          })}
          <div style={st.totalRow}>Today total: {Number(todayTotal).toFixed(2)} hrs</div>
        </div>
      )}

      {/* === RECENT DAYS === */}
      {recentDays.length > 0 && (
        <div style={st.recentSection}>
          <div style={st.recentTitle}>RECENT DAYS</div>
          {recentDays.map(function(day) {
            var isExpanded = expandedDay === day.date
            var dt = new Date(day.date + 'T12:00:00')
            var dateLabel = dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
            return (
              <div key={day.date}>
                <div style={st.recentRow} onClick={function() { toggleDay(day.date) }}>
                  <div style={st.recentDate}>{dateLabel}</div>
                  <div style={{ flex: 1, fontSize: '12px', color: 'rgba(255,255,255,0.35)' }}>{day.count} session{day.count > 1 ? 's' : ''}</div>
                  <div style={st.recentHrs}>{Number(day.total).toFixed(2)} hrs</div>
                  <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '12px' }}>{isExpanded ? '\u25B2' : '\u25BC'}</span>
                </div>
                {isExpanded && (
                  <div style={st.expandedBox}>
                    {(expandedDay === day.date ? day.sessions : expandedSessions).map(function(s) {
                      var threeDaysAgo2 = getDaysAgoEastern(3)
                      var canEdit2 = s.work_date >= threeDaysAgo2
                      return (
                        <div key={s.id} style={st.sessionRow}>
                          <span style={st.sessionLabel}>Session {s.session_number}:</span>
                          {canEdit2 ? <button onClick={function() { setEditModal({ record: s, field: 'clock_in' }) }} style={st.goldTime}>{fmtET(s.clock_in)}</button> : <span style={{ fontSize: '13px', color: '#22c55e' }}>{fmtET(s.clock_in)}</span>}
                          <span style={{ color: 'rgba(255,255,255,0.15)', margin: '0 4px' }}>\u2014</span>
                          {canEdit2 && s.clock_out ? <button onClick={function() { setEditModal({ record: s, field: 'clock_out' }) }} style={st.goldTime}>{fmtET(s.clock_out)}</button> : <span style={{ fontSize: '13px', color: '#ef4444' }}>{fmtET(s.clock_out)}</span>}
                          <span style={st.sessionHrs}>({Number(s.total_hours || 0).toFixed(2)} hrs)</span>
                          {s.edited_at && <span style={st.editedBadge}>Edited</span>}
                          {!canEdit2 && <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.15)', fontStyle: 'italic' }}>Contact owner</span>}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {editModal && <TimeEditModal record={editModal.record} field={editModal.field} onSave={function() { setEditModal(null); fetchAll() }} onClose={function() { setEditModal(null) }} />}
      <style>{'@keyframes dci-spin { to { transform: rotate(360deg) } }'}</style>
    </div>
  )
}

var st = {
  wrap: { marginBottom: '20px' },
  errorBox: { padding: '10px 14px', backgroundColor: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', color: '#f87171', fontSize: '13px', marginBottom: '12px', textAlign: 'center' },
  btn: { width: '100%', padding: '0 20px', border: 'none', borderRadius: '8px', color: '#fff', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '20px', fontWeight: 700, letterSpacing: '3px', cursor: 'pointer', minHeight: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  spinRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  spinner: { display: 'inline-block', width: '20px', height: '20px', border: '3px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'dci-spin 0.6s linear infinite' },
  sessionsBox: { marginTop: '16px', padding: '14px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px' },
  sessionsTitle: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '2px', color: '#b48c3c', marginBottom: '10px' },
  sessionRow: { display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', flexWrap: 'wrap' },
  sessionLabel: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '1px', color: 'rgba(255,255,255,0.4)', minWidth: '70px' },
  sessionHrs: { fontSize: '12px', color: 'rgba(255,255,255,0.3)', marginLeft: '4px' },
  totalRow: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '14px', fontWeight: 700, color: '#fff', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.08)' },
  goldTime: { background: 'none', border: 'none', borderBottom: '1px dashed #b48c3c', color: '#b48c3c', fontSize: '13px', fontWeight: 600, fontFamily: "'Barlow',sans-serif", cursor: 'pointer', padding: '0 1px', lineHeight: '1.4' },
  editedBadge: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '9px', fontWeight: 700, letterSpacing: '1px', color: '#b48c3c', backgroundColor: 'rgba(180,140,60,0.15)', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '3px', padding: '2px 6px', marginLeft: '4px' },
  recentSection: { marginTop: '20px' },
  recentTitle: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '8px' },
  recentRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '4px', marginBottom: '2px', cursor: 'pointer' },
  recentDate: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.5)', minWidth: '100px' },
  recentHrs: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '14px', fontWeight: 700, color: '#fff' },
  expandedBox: { padding: '8px 12px 8px 24px', marginBottom: '4px', backgroundColor: 'rgba(255,255,255,0.01)', borderLeft: '2px solid rgba(180,140,60,0.2)' },
}
